import React, { useState, useEffect, useRef } from 'react';
import { colors, typography, layout } from '../articleMinimalStyles';

// Helper function to generate a slug (simple version)
const slugify = (text) => text.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]+/g, '');

const QuickLinks = ({ content }) => {
  const [headings, setHeadings] = useState([]);
  const [activeId, setActiveId] = useState(''); // State for active link highlighting
  const observer = useRef(null); // Intersection Observer ref

  // Extract headings from HTML content (Basic Example)
  // This is fragile and best done with structured data or server-side processing
  useEffect(() => {
    const extractHeadings = () => {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = content || '';
        const foundHeadings = [];
        tempDiv.querySelectorAll('h2, h3').forEach((h) => {
            const text = h.textContent || '';
            const level = h.tagName === 'H2' ? 2 : 3;
            const id = h.id || slugify(text); // Use existing ID or generate one
            if (!h.id) h.id = id; // Add ID if missing (won't affect original content string here)
            foundHeadings.push({ id, text, level });
        });
        setHeadings(foundHeadings);
    };
    extractHeadings();
    // Clean up tempDiv? Not strictly necessary here.
  }, [content]);

   // --- Active Link Highlighting (More Complex Logic) ---
   // This requires IntersectionObserver to watch sections in the ArticleBody
   // For simplicity in this template, we'll comment out the active highlighting part
   /*
   useEffect(() => {
       // Disconnect previous observer if headings change
       if (observer.current) {
           observer.current.disconnect();
       }

       // Callback function for Intersection Observer
       const observerCallback = (entries) => {
           entries.forEach(entry => {
               if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
                   // Prioritize entry that is most visible if multiple are intersecting
                   setActiveId(entry.target.id);
               }
               // Optional: handle scrolling up to de-highlight or highlight previous
           });
       };

       observer.current = new IntersectionObserver(observerCallback, {
           rootMargin: '0px 0px -40% 0px', // Trigger when heading is in upper 60% of viewport
           threshold: 0.5 // At least 50% visible
       });

       const articleBody = document.querySelector('.article-content-for-observer'); // Need a way to target ArticleBody
       if (articleBody) {
            headings.forEach(({ id }) => {
                const headingElement = articleBody.querySelector(`#${id}`);
                if (headingElement) {
                   observer.current.observe(headingElement);
                }
            });
       }

       // Cleanup observer on unmount
       return () => {
           if (observer.current) {
              observer.current.disconnect();
           }
       };

   }, [headings]); // Rerun observer setup if headings change
   */
   // --- End Active Link Highlighting ---


  const styles = {
    stickyPanel: {
      position: 'sticky',
      top: '120px', // Adjust based on header height + desired gap
      maxHeight: 'calc(100vh - 140px)', // Limit height
      overflowY: 'auto',
      paddingLeft: '25px', // Space from main content
      borderLeft: `1px solid ${colors.border}`,
      alignSelf: 'start', // Align to top of flex/grid container
      // Hide on smaller screens where layout breaks (requires JS width check or CSS)
      // display: windowWidth > someBreakpoint ? 'block' : 'none',
    },
    panelTitle: {
      fontSize: '0.9rem',
      fontWeight: typography.boldWeight,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      color: colors.secondaryText,
      marginBottom: '15px',
    },
    linkList: {
      listStyle: 'none',
      padding: 0,
      margin: 0,
    },
    linkItemBase: {
      marginBottom: '8px',
    },
    link: {
      fontSize: typography.smallSize,
      color: colors.secondaryText,
      textDecoration: 'none',
      lineHeight: 1.5,
      display: 'block',
      transition: 'color 0.2s ease, transform 0.2s ease',
      transformOrigin: 'left center',
    },
    linkHover: {
      color: colors.accent,
      transform: 'translateX(3px)',
    },
    linkActive: { // Style for the active link (if activeId state is used)
      color: colors.accent,
      fontWeight: typography.boldWeight,
    },
    // Indentation for different levels
    linkLevel2: { paddingLeft: '0px' }, // Base level
    linkLevel3: { paddingLeft: '15px' }, // Indent H3s
  };

   // State for hover effect
   const [hoveredLink, setHoveredLink] = useState(null);

  if (headings.length === 0) return null; // Don't render if no headings found

  return (
    <nav style={styles.stickyPanel} aria-label="Article Quick Links">
      <h3 style={styles.panelTitle}>On this page</h3>
      <ul style={styles.linkList}>
        {headings.map((heading, index) => {
            const isHovered = hoveredLink === heading.id;
            const isActive = activeId === heading.id; // Use state for active link
            const linkStyle = {
                ...styles.link,
                ...(heading.level === 3 ? styles.linkLevel3 : styles.linkLevel2),
                ...(isActive ? styles.linkActive : {}), // Apply active style
                ...(isHovered && !isActive ? styles.linkHover : {}), // Apply hover only if not active
            };
          return (
            <li key={heading.id || index} style={styles.linkItemBase}>
              <a
                href={`#${heading.id}`} // Simple anchor link
                style={linkStyle}
                onMouseEnter={() => setHoveredLink(heading.id)}
                onMouseLeave={() => setHoveredLink(null)}
                // onClick={(e) => { // Optional smooth scroll
                //   e.preventDefault();
                //   document.getElementById(heading.id)?.scrollIntoView({ behavior: 'smooth' });
                //   // Update URL hash without page jump (optional)
                //   window.history.pushState(null, '', `#${heading.id}`);
                // }}
              >
                {heading.text}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

export default QuickLinks;